import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import useSWR from "swr";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { InstallerLogo, installerBranding, appConfig } from "@/branding/Branding";
import NotificationBell from "@/components/notifications/NotificationBell";
import {
    LayoutGrid, Users, FolderOpen, Calendar, Wrench, FileText,
    BarChart3, ShieldCheck, LogOut, Menu, X, Bell, BellRing, AlertTriangle,
    ClipboardList, ListTodo,
} from "lucide-react";
import { HardHat } from "lucide-react";
import { portalIdentity } from "@/lib/portalIdentity";

/**
 * OperationsShell — dedicated portal chrome for the new Operations role.
 *
 * OPS-003B5 responsive matrix:
 *   * < 768px  (mobile phone)         — fixed top bar + OVERLAY drawer
 *                                        (backdrop, body scroll lock,
 *                                         focus trap, focus return)
 *   * 768–1279 (tablet / iPad)        — sticky top bar + PUSH sidebar
 *                                        (no backdrop, no scroll lock,
 *                                         no focus trap — background
 *                                         stays visible & usable;
 *                                         content resizes via flex)
 *   * ≥ 1280px (desktop)              — permanent expanded sidebar
 *
 * The mobile drawer and the tablet push sidebar are DIFFERENT
 * surfaces with DIFFERENT accessibility contracts. Splitting the
 * state (`mobileDrawer` vs `tabletOpen`) prevents one contract from
 * bleeding into the other during future refactors — e.g. accidentally
 * locking body scroll on the tablet push panel.
 *
 * Nav visibility is permission-aware: the Administration section only
 * renders when the user carries at least one admin-capable permission.
 */
const NAV = [
    { key: "dashboard",     label: "Dashboard",     icon: LayoutGrid,     to: "/operations", end: true },
    { key: "attention",     label: "Attention",     icon: AlertTriangle,  to: "/operations/attention" },
    { key: "customers",     label: "Customers",     icon: Users,          to: "/operations/customers" },
    { key: "projects",      label: "Projects",      icon: FolderOpen,     to: "/operations/projects" },
    { key: "calendar",      label: "Calendar",      icon: Calendar,       to: "/operations/calendar" },
    // FIELD-001C2B · Ops sidebar entry surfacing the existing Tasks
    // workspace (`/operations/tasks`). Authorised task creation +
    // multi-assignee controls live on that page.
    { key: "tasks",         label: "Tasks",         icon: ClipboardList,  to: "/operations/tasks" },
    // FIELD-001C1A / C2B · Resource Management uses the shared
    // person-wearing-a-hard-hat icon consistently across sidebar,
    // dashboard shortcut, admin tile and Field Team heading.
    { key: "resources",     label: "Resource Management", icon: HardHat, to: "/operations/resources" },
    { key: "documents",     label: "Documents",     icon: FileText,       to: "/operations/documents" },
    { key: "reports",       label: "Reports",       icon: BarChart3,      to: "/operations/reports" },
];

const ADMIN_ITEM = {
    key: "administration", label: "Administration",
    icon: ShieldCheck, to: "/operations/administration",
};

// SALES-ROLE-001A · Administration navigation now gates on TRUE
// admin capabilities only. `manage_calendar` and `view_reports`
// remain permission primitives (Sales carries both) but do NOT
// count as administrative capability — they only govern access to
// the calendar/report READ surfaces. Backend permission guards on
// individual admin routes are unchanged; this predicate only
// controls whether the "Administration" sidebar section renders.
const ADMIN_CAPABILITIES = new Set([
    "manage_users", "manage_projects", "manage_templates",
    "manage_system_settings", "manage_engineers",
]);

// SALES-ROLE-001A · NAV items the Sales role must not see in the
// sidebar. The corresponding backend endpoints stay guarded on
// their own permissions — this list is a UX filter only.
// SALES-ROLE-001B · Reports added — the fleet-wide Operations
// Reports & Control Hub is Operations-only unless explicitly
// authorised; a Sales user must not see the nav entry.
// DEV-023 · Notifications moved into Administration (`/operations/notifications`
// is now surfaced as an admin console card, not a standalone sidebar
// entry) — see `OperationsAdministration.jsx` for the new placement.
const SALES_HIDDEN_NAV = new Set([
    "attention", "resources", "documents", "reports",
]);

// OPS-UX-001A1 · Session-scoped persistence for the tablet push
// sidebar's open/closed state. sessionStorage is TAB-scoped so the
// preference survives route-driven remounts of OperationsShell but
// does not leak between browser tabs or reboots. Access is guarded
// so SSR, private modes and disabled storage never throw.
const TABLET_SIDEBAR_STORAGE_KEY = "sf.ops.tabletSidebarOpen";

function readTabletSidebarPref() {
    try {
        if (typeof window === "undefined" || !window.sessionStorage) return false;
        return window.sessionStorage.getItem(TABLET_SIDEBAR_STORAGE_KEY) === "1";
    } catch (_) {
        return false;
    }
}

function writeTabletSidebarPref(open) {
    try {
        if (typeof window === "undefined" || !window.sessionStorage) return;
        window.sessionStorage.setItem(TABLET_SIDEBAR_STORAGE_KEY, open ? "1" : "0");
    } catch (_) {
        // Private mode, quota exceeded, storage disabled — silent
        // fail is safe: React state still drives the visible aside.
    }
}

function hasAnyAdminPerm(user) {
    // SALES-ROLE-001A · Sales must NEVER see the Administration
    // section, even if a future admin decides to grant them a
    // permission that happens to overlap. Guard on role first,
    // then intersect on the tightened ADMIN_CAPABILITIES set.
    if (user?.role === "sales") return false;
    const perms = user?.permissions || [];
    return perms.some((p) => ADMIN_CAPABILITIES.has(p));
}

function visibleNavForRole(role) {
    if (role === "sales") {
        // SALES-ROLE-001B · Sales workspace uses "Pipeline"
        // terminology; deep-link keeps the shared authorised
        // ``/operations/projects`` route + records — Sales does NOT
        // get a parallel model.
        //
        // DEV-019 UAT correction 4 · Sales Tasks nav MUST route to
        // the canonical ``/sales/tasks`` — never
        // ``/operations/tasks`` — even though the underlying page
        // component is the same. Bypasses the "your Sales work is
        // in the Operations workspace" confusion Codex UAT flagged.
        //
        // DEV-019G · Sales sidebar gains a dedicated "To-do" entry
        // for the existing Sales Actions workspace, inserted
        // directly after Tasks so the two Sales-facing surfaces
        // sit side-by-side while remaining distinct concepts:
        // Tasks = formal project/workflow tasks, To-do = Sales
        // Actions.
        const TODO_ITEM = {
            key: "todo", label: "To-do", icon: ListTodo,
            to: "/sales/todo",
        };
        const relabelled = NAV.map((item) => {
            if (item.key === "projects") return { ...item, label: "Pipeline" };
            if (item.key === "tasks")    return { ...item, to: "/sales/tasks" };
            return item;
        });
        const filtered = relabelled.filter((item) => !SALES_HIDDEN_NAV.has(item.key));
        const idx = filtered.findIndex((item) => item.key === "tasks");
        if (idx >= 0) filtered.splice(idx + 1, 0, TODO_ITEM);
        else filtered.push(TODO_ITEM);
        return filtered;
    }
    return NAV;
}

function SidebarBody({ user, logout, navigate, variant = "expanded", onNavigate, attentionCount, notifBadge }) {
    const rail = variant === "rail";
    const identity = portalIdentity(user?.role);
    const initials = (user?.name || user?.email || "?")
        .split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

    return (
        <>
            <div className={`border-b border-white/[0.08] shrink-0 ${rail ? "px-2 pt-4 pb-3" : "px-4 pt-6 pb-4"}`}>
                <Link
                    to={identity.homePath}
                    onClick={onNavigate}
                    className={`inline-flex bg-white rounded-md ${rail ? "p-1.5 w-full justify-center" : "px-3 py-2"}`}
                    aria-label={appConfig.name}
                >
                    <InstallerLogo height={rail ? 24 : 36} />
                </Link>
                {!rail && (
                    <>
                        <div className="text-white text-base font-medium tracking-[0.02em] mt-3">{appConfig.name}</div>
                        <div
                            className="text-white/55 text-[11px] tracking-[0.14em] uppercase mt-0.5"
                            data-testid="ops-shell-portal-label"
                        >
                            {identity.portalLabel}
                        </div>
                    </>
                )}
            </div>

            <nav className="flex-1 min-h-0 overflow-y-auto px-2 py-4">
                {!rail && (
                    <div className="text-white/60 text-[11px] font-semibold tracking-[0.14em] px-3 mb-2">WORKSPACE</div>
                )}
                {visibleNavForRole(user?.role).map((item) => (
                    <NavItem
                        key={item.key} item={item} rail={rail} onNavigate={onNavigate}
                        badge={
                            item.key === "attention" ? attentionCount :
                            item.key === "notifications" ? notifBadge :
                            undefined
                        }
                    />
                ))}
                {hasAnyAdminPerm(user) && (
                    <>
                        {!rail && (
                            <div className="text-white/60 text-[11px] font-semibold tracking-[0.14em] px-3 mt-6 mb-2">
                                ADMINISTRATION
                            </div>
                        )}
                        <NavItem item={ADMIN_ITEM} rail={rail} onNavigate={onNavigate} />
                    </>
                )}
            </nav>

            <div
                className={`space-y-2 shrink-0 ${rail ? "p-2" : "p-3"}`}
                style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
                data-testid="sidebar-footer"
            >
                {rail ? (
                    <>
                        <Link
                            to="/profile"
                            data-testid="ops-sidebar-profile"
                            className="w-11 h-11 mx-auto rounded-full bg-[#3E9E2E] text-white text-xs font-medium flex items-center justify-center"
                            aria-label="Open your account"
                        >
                            {initials}
                        </Link>
                        <div className="w-11 h-11 mx-auto flex items-center justify-center">
                            <NotificationBell testidPrefix="ops-notif-rail" />
                        </div>
                        <button
                            data-testid="sidebar-logout"
                            onClick={() => { logout(); navigate("/"); }}
                            className="w-11 h-11 mx-auto rounded-md flex items-center justify-center text-white/85 hover:bg-white/[0.12] transition-colors"
                            aria-label="Sign out"
                        >
                            <LogOut size={16} strokeWidth={1.75} />
                        </button>
                    </>
                ) : (
                    <>
                        <div className="flex items-center gap-1 p-2 rounded-md bg-white/[0.04] hover:bg-white/[0.08] transition-colors">
                            <Link to="/profile" data-testid="ops-sidebar-profile" className="flex items-center gap-2.5 flex-1 min-w-0" aria-label="Open your account">
                                <div className="w-9 h-9 rounded-full bg-[#3E9E2E] text-white text-xs font-medium flex items-center justify-center shrink-0">{initials}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-white text-xs font-medium truncate">{user?.name || "Signed in"}</div>
                                    <div className="text-white/55 text-[10px] truncate">{user?.email}</div>
                                </div>
                            </Link>
                            <NotificationBell testidPrefix="ops-notif" />
                            <button
                                data-testid="sidebar-logout"
                                onClick={() => { logout(); navigate("/"); }}
                                className="w-9 h-9 rounded-full flex items-center justify-center text-white/85 hover:bg-white/[0.12] transition-colors shrink-0"
                                aria-label="Sign out"
                            >
                                <LogOut size={14} strokeWidth={1.75} />
                            </button>
                        </div>
                        <div className="text-center text-white/30 text-[10px] tracking-[0.08em]">
                            Powered by {installerBranding.name}
                        </div>
                    </>
                )}
            </div>
        </>
    );
}

function NavItem({ item, rail, onNavigate, badge }) {
    // OPS-003B3 · Compact numeric badge with accessible full-count
    // label. The visible text collapses to `99+` above 99, but the
    // aria-label ALWAYS carries the true count so screen readers
    // announce the real number (e.g. "127 active Attention Centre
    // exceptions").
    const badgeVisible = badge != null && Number.isFinite(badge) && badge > 0;
    const displayCount = badgeVisible && badge > 99 ? "99+" : String(badge);
    const accessibleLabel = badgeVisible
        ? `${badge} active ${item.label} Centre exceptions`.replace(
              /\s{2,}/g, " ",
          )
        : undefined;
    // Testids only on the expanded sidebar so tests target a single
    // element regardless of viewport width. The rail variant uses the
    // same NavLink but omits the testid — the enterprise sidebar always
    // renders both shells at once and hides one via CSS.
    return (
        <NavLink
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            data-testid={rail ? undefined : `ops-nav-${item.key}`}
            title={rail ? item.label : undefined}
            aria-label={item.label}
            className={({ isActive }) =>
                // OPS-003B3 · `relative` is CRITICAL on the rail
                // variant so the absolutely-positioned badge anchors
                // to THIS icon container. Without it the badge
                // escapes upward and lands on top of the logo.
                //
                // OPS-UX-001 · Selected state gets a clear Pearl-green
                // treatment (background + left indicator + high-
                // contrast text) so it reads unambiguously against the
                // lighter navy. Hover and focus stay distinct.
                `group relative flex items-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3E9E2E]/60 ${
                    rail ? "justify-center h-11 w-11 mx-auto my-1"
                         : "gap-2.5 px-3 py-2.5 min-h-[44px]"
                } ${
                    isActive
                        ? "bg-[#3E9E2E]/22 border border-[#3E9E2E]/55 text-white shadow-[inset_2px_0_0_#3E9E2E]"
                        : "text-white/85 hover:text-white hover:bg-white/[0.08] border border-transparent"
                }`
            }
        >
            <item.icon size={rail ? 18 : 16} strokeWidth={1.75} />
            {!rail && <span className="text-sm font-medium flex-1">{item.label}</span>}
            {badgeVisible && (
                <span
                    data-testid={rail
                        ? `ops-nav-${item.key}-badge-rail`
                        : `ops-nav-${item.key}-badge`}
                    aria-label={accessibleLabel}
                    className={
                        // Rail badge sits at the icon container's
                        // top-right, clamped inside the 44×44 hit
                        // target so hover/focus rings never cover it.
                        rail
                            ? "absolute -top-0.5 -right-0.5 inline-flex items-center justify-center rounded-full bg-[#DC2626] text-white text-[9px] font-semibold min-w-[16px] h-[16px] px-1 pointer-events-none ring-2 ring-[#152244]"
                            : "inline-flex items-center justify-center rounded-full bg-[#DC2626] text-white text-[10px] font-semibold min-w-5 h-5 px-1.5"
                    }
                >{displayCount}</span>
            )}
        </NavLink>
    );
}

export default function OperationsShell({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    // OPS-003B5 · Split state — the mobile drawer is a MODAL overlay
    // (backdrop + body scroll lock + focus trap). The tablet sidebar
    // is a PUSH panel that shares the layout with content (no
    // backdrop, no scroll lock, no focus trap). Keeping them
    // independent stops one refactor from bleeding accessibility
    // contracts across breakpoints.
    //
    // OPS-UX-001A1 · Every /operations/* page currently wraps itself
    // in <OperationsShell>, so React Router remounts a FRESH shell on
    // every route change. That reset was collapsing the tablet push
    // sidebar on navigation. Persist ONLY the tablet open/closed flag
    // in sessionStorage so the new instance restores the last chosen
    // state. Mobile drawer and desktop permanent sidebar are
    // intentionally NOT persisted — the mobile drawer is a temporary
    // overlay that must always auto-close after navigation, and the
    // desktop sidebar is permanently visible.
    const [mobileDrawer, setMobileDrawer] = useState(false);
    const [tabletOpen, setTabletOpen] = useState(readTabletSidebarPref);

    const menuButtonRef = useRef(null);
    const tabletMenuButtonRef = useRef(null);
    const drawerRef = useRef(null);
    const tabletSidebarRef = useRef(null);

    const openMobileDrawer = useCallback(() => setMobileDrawer(true), []);
    const closeMobileDrawer = useCallback(() => setMobileDrawer(false), []);
    const setTabletOpenPersisted = useCallback((next) => {
        setTabletOpen((prev) => {
            const value = typeof next === "function" ? next(prev) : next;
            writeTabletSidebarPref(value);
            return value;
        });
    }, []);
    const toggleTabletSidebar = useCallback(
        () => setTabletOpenPersisted((v) => !v),
        [setTabletOpenPersisted],
    );
    const closeTabletSidebar = useCallback(
        () => setTabletOpenPersisted(false),
        [setTabletOpenPersisted],
    );

    // Escape closes whichever surface is currently open. Handled at
    // the window level (both surfaces are dismissible by keyboard
    // even when focus has moved outside the panel — required for
    // the push sidebar because focus can freely roam into main).
    useEffect(() => {
        if (!mobileDrawer && !tabletOpen) return undefined;
        const onKey = (e) => {
            if (e.key !== "Escape") return;
            e.preventDefault();
            if (mobileDrawer) closeMobileDrawer();
            if (tabletOpen) closeTabletSidebar();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [mobileDrawer, tabletOpen, closeMobileDrawer, closeTabletSidebar]);

    // Body scroll lock ONLY for the mobile overlay drawer. The
    // tablet push sidebar shares the viewport with content, so the
    // page must remain scrollable behind it (or scrolling breaks
    // reading long project detail pages on iPad).
    useEffect(() => {
        if (!mobileDrawer) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [mobileDrawer]);

    // Mobile drawer: focus-trap + focus-return. Unchanged from
    // OPS-003B4 apart from being scoped to `mobileDrawer` only.
    useEffect(() => {
        if (!mobileDrawer) return undefined;
        const drawerEl = drawerRef.current;
        if (!drawerEl) return undefined;
        const first = drawerEl.querySelector("[data-testid=ops-drawer-close]");
        if (first) first.focus();
        const onTrap = (e) => {
            if (e.key !== "Tab") return;
            const focusables = drawerEl.querySelectorAll(
                "a[href], button:not([disabled]), [tabindex]:not([tabindex='-1'])",
            );
            if (focusables.length === 0) return;
            const firstFocusable = focusables[0];
            const lastFocusable = focusables[focusables.length - 1];
            if (e.shiftKey && document.activeElement === firstFocusable) {
                e.preventDefault(); lastFocusable.focus();
            } else if (!e.shiftKey && document.activeElement === lastFocusable) {
                e.preventDefault(); firstFocusable.focus();
            }
        };
        drawerEl.addEventListener("keydown", onTrap);
        // Snapshot the menu button at effect-run time so cleanup can
        // restore focus to the same node (react-hooks/exhaustive-deps).
        const menuBtnEl = menuButtonRef.current;
        return () => {
            drawerEl.removeEventListener("keydown", onTrap);
            if (menuBtnEl && typeof menuBtnEl.focus === "function") {
                menuBtnEl.focus();
            }
        };
    }, [mobileDrawer]);

    // OPS-003B5 · Tablet push sidebar — move focus to the close
    // button on open, return focus to the tablet menu button on
    // close. NO focus trap: the background stays usable.
    useEffect(() => {
        if (!tabletOpen) return undefined;
        const el = tabletSidebarRef.current;
        const close = el && el.querySelector("[data-testid=ops-tablet-sidebar-close]");
        if (close && typeof close.focus === "function") close.focus();
        // Snapshot the tablet menu button at effect-run time so cleanup
        // restores focus to the same node (react-hooks/exhaustive-deps).
        const tabletMenuBtnEl = tabletMenuButtonRef.current;
        return () => {
            if (tabletMenuBtnEl && typeof tabletMenuBtnEl.focus === "function") {
                tabletMenuBtnEl.focus();
            }
        };
    }, [tabletOpen]);

    // OPS-003 · Live active-exception count for the Attention nav badge.
    // Refresh every 60s; SWR handles retry/backoff. Failures are silent —
    // a missing badge is preferable to breaking the whole shell.
    const { data: summary } = useSWR(
        "attention-summary",
        () => api.operationsExceptionsSummary(),
        { refreshInterval: 60_000, revalidateOnFocus: true, shouldRetryOnError: false },
    );
    const attentionCount = summary?.active_total ?? 0;
    // NOT-001C · Notification Centre health badge — count of failed +
    // retryable deliveries in the last 7 days. Failures are silent so
    // the shell keeps rendering even if the endpoint hiccups.
    const since7d = React.useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return d.toISOString();
    }, []);
    const { data: notifSummary } = useSWR(
        ["ops-notif-summary", since7d],
        () => api.opsNotifSummary({ since: since7d }),
        { refreshInterval: 60_000, revalidateOnFocus: true, shouldRetryOnError: false },
    );
    const notifBadge =
        (notifSummary?.deliveries?.failed ?? 0)
        + (notifSummary?.deliveries?.retryable ?? 0);

    // OPS-UX-001 · Shared premium Pearl navy — a lighter, richer
    // gradient than the previous near-black. Used on the desktop
    // sidebar, tablet push sidebar and both top bars so sidebar +
    // header stay clearly related without feeling like an
    // uninterrupted black slab.
    const NAVY_GRADIENT = "linear-gradient(180deg,#152244 0%,#1D2E5C 55%,#1F3A78 100%)";
    const NAVY_SOLID    = "#152244";
    // DEV-022 · Role-aware top-bar label. A Sales session must
    // read "Sales", NOT "Operations" — the previous shell hard-
    // coded the label so every non-Operations role saw the wrong
    // portal identity.
    const identity = portalIdentity(user?.role);

    return (
        <div className="min-h-screen bg-[#F5F5F7] flex" data-testid="operations-shell">
            {/* Desktop permanent sidebar — ≥ 1280px. Unchanged. */}
            <aside
                data-testid="ops-sidebar"
                className="hidden xl:flex xl:flex-col xl:sticky xl:top-0 xl:h-screen xl:self-start w-[240px] 2xl:w-[260px] shrink-0 border-r border-white/[0.06]"
                style={{ background: NAVY_GRADIENT }}
            >
                <SidebarBody user={user} logout={logout} navigate={navigate} variant="expanded" attentionCount={attentionCount} notifBadge={notifBadge} />
            </aside>

            {/* OPS-003B5 · Tablet PUSH sidebar — 768–1279px.
                Shares the row with the content column: opening it
                animates width 0 → 260px which naturally pushes the
                content column right and resizes it via flex. No
                backdrop, no scroll lock, no overlay. The inner
                fixed-width wrapper keeps the sidebar chrome
                un-squashed while the outer aside collapses to 0. */}
            <aside
                ref={tabletSidebarRef}
                id="ops-tablet-sidebar"
                data-testid="ops-tablet-sidebar"
                aria-hidden={!tabletOpen}
                aria-label="Operations navigation"
                className={`hidden md:flex xl:hidden md:flex-col md:sticky md:top-0 md:h-screen md:self-start shrink-0 overflow-hidden border-r border-white/[0.06] transition-[width] duration-300 ease-out motion-reduce:transition-none ${tabletOpen ? "w-[260px]" : "w-0"}`}
                style={{ background: NAVY_GRADIENT }}
            >
                <div className="w-[260px] flex flex-col h-full relative">
                    <button
                        data-testid="ops-tablet-sidebar-close"
                        onClick={closeTabletSidebar}
                        tabIndex={tabletOpen ? 0 : -1}
                        className="absolute top-3 right-3 z-10 w-10 h-10 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/[0.08]"
                        aria-label="Close menu"
                    >
                        <X size={18} strokeWidth={1.75} />
                    </button>
                    {/* OPS-UX-001A · Tablet push sidebar PERSISTS on
                        navigation — no onNavigate is wired so
                        NavLink clicks change route without
                        collapsing the panel. The user closes it
                        explicitly via the close button, the tablet
                        menu toggle or Escape. */}
                    <SidebarBody
                        user={user} logout={logout} navigate={navigate}
                        variant="expanded"
                        attentionCount={attentionCount} notifBadge={notifBadge}
                    />
                </div>
            </aside>

            {/* Content column — flex sibling that grows/shrinks as
                the tablet aside opens. `min-w-0` prevents flex
                overflow from long content pushing horizontal
                scroll. */}
            <div className="flex-1 min-w-0 flex flex-col" data-testid="operations-content-column">
                {/* Mobile top bar — < 768px. Fixed so main content
                    still needs pt-14 padding to clear it. */}
                <div className="md:hidden fixed top-0 left-0 right-0 z-30 h-14 flex items-center px-4 gap-3" style={{ background: NAVY_SOLID }}>
                    <button
                        ref={menuButtonRef}
                        data-testid="ops-mobile-menu"
                        onClick={openMobileDrawer}
                        className="w-11 h-11 -ml-2 flex items-center justify-center text-white/85 hover:text-white"
                        aria-label="Open menu"
                        aria-expanded={mobileDrawer}
                        aria-controls="ops-nav-drawer"
                    >
                        <Menu size={20} strokeWidth={1.75} />
                    </button>
                    <div className="bg-white rounded-md px-2 py-1"><InstallerLogo height={20} /></div>
                    <span
                        className="text-white text-sm font-medium"
                        data-testid="ops-shell-topbar-label-mobile"
                    >
                        {identity.topbarLabel}
                    </span>
                    <div className="flex-1" />
                    <NotificationBell testidPrefix="ops-notif-mobile" />
                    <button
                        onClick={() => { logout(); navigate("/"); }}
                        data-testid="ops-mobile-logout"
                        className="w-11 h-11 -mr-2 flex items-center justify-center text-white/85 hover:text-white"
                        aria-label="Sign out"
                    >
                        <LogOut size={16} strokeWidth={1.75} />
                    </button>
                </div>

                {/* Tablet top bar — 768–1279px. Lives INSIDE the
                    content column so it shifts right together with
                    main when the push sidebar opens. Sticky top-0
                    keeps it visible during vertical scroll. */}
                <div className="hidden md:flex xl:hidden sticky top-0 z-20 h-14 items-center px-4 gap-3 shrink-0" style={{ background: NAVY_SOLID }}>
                    <button
                        ref={tabletMenuButtonRef}
                        data-testid="ops-tablet-menu"
                        onClick={toggleTabletSidebar}
                        className="w-11 h-11 -ml-2 flex items-center justify-center text-white/85 hover:text-white"
                        aria-label={tabletOpen ? "Close menu" : "Open menu"}
                        aria-expanded={tabletOpen}
                        aria-controls="ops-tablet-sidebar"
                    >
                        <Menu size={20} strokeWidth={1.75} />
                    </button>
                    <div className="bg-white rounded-md px-2 py-1"><InstallerLogo height={22} /></div>
                    <span
                        className="text-white text-sm font-medium"
                        data-testid="ops-shell-topbar-label-tablet"
                    >
                        {identity.topbarLabel}
                    </span>
                    <div className="flex-1" />
                    {/* Compact Attention indicator in the tablet top
                        bar so at-a-glance count is visible even
                        before the sidebar is opened. */}
                    {attentionCount > 0 && (
                        <span
                            data-testid="ops-tablet-attention-count"
                            aria-label={`${attentionCount} active Attention Centre exceptions`}
                            className="inline-flex items-center gap-1 px-2 h-6 rounded-full bg-[#DC2626] text-white text-[11px] font-semibold"
                        >
                            <AlertTriangle size={12} strokeWidth={2} />
                            {attentionCount > 99 ? "99+" : attentionCount}
                        </span>
                    )}
                    <NotificationBell testidPrefix="ops-notif-tablet" />
                    <button
                        onClick={() => { logout(); navigate("/"); }}
                        data-testid="ops-tablet-logout"
                        className="w-11 h-11 -mr-2 flex items-center justify-center text-white/85 hover:text-white"
                        aria-label="Sign out"
                    >
                        <LogOut size={16} strokeWidth={1.75} />
                    </button>
                </div>

                <main className="flex-1 min-w-0 pt-14 md:pt-0" data-testid="operations-main">
                    {children}
                </main>
            </div>

            {/* Mobile overlay drawer — < 768px only. Preserved from
                OPS-003B4: backdrop, body scroll lock, focus trap,
                focus return. */}
            {mobileDrawer && (
                <div
                    className="md:hidden fixed inset-0 z-40"
                    data-testid="ops-mobile-drawer"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Operations navigation"
                    id="ops-nav-drawer"
                >
                    <div
                        data-testid="ops-drawer-backdrop"
                        className="absolute inset-0 bg-black/50"
                        onClick={closeMobileDrawer}
                    />
                    <aside
                        ref={drawerRef}
                        className="absolute left-0 top-0 bottom-0 w-[280px] max-w-[85vw] flex flex-col shadow-2xl"
                        style={{ background: "#0B1220" }}
                    >
                        <button
                            data-testid="ops-drawer-close"
                            onClick={closeMobileDrawer}
                            className="absolute top-3 right-3 text-white/70 hover:text-white z-10 w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/[0.08]"
                            aria-label="Close menu"
                        >
                            <X size={18} strokeWidth={1.75} />
                        </button>
                        <SidebarBody
                            user={user} logout={logout} navigate={navigate}
                            variant="drawer"
                            onNavigate={closeMobileDrawer}
                            attentionCount={attentionCount} notifBadge={notifBadge}
                        />
                    </aside>
                </div>
            )}
        </div>
    );
}
